import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTickets } from '@/presentation/hooks/useTickets';
import { useTenants } from '@/presentation/hooks/useTenants';
import { useToast } from '@/presentation/contexts/ToastContext';
import { Button, ModalShell } from '@/presentation/components/common';
import type { SupportTicketDTO, SupportTicketStatus } from '@/domain/repositories/ISupportTicketRepository';

const STATUS_LABELS: Record<SupportTicketStatus, string> = {
  pendiente: 'Pendiente',
  en_proceso: 'En Proceso',
  resuelto: 'Resuelto',
};

const STATUS_COLORS: Record<SupportTicketStatus, string> = {
  pendiente: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  en_proceso: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
  resuelto: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
};

// ─── Ticket Detail Modal ───────────────────────────────────────────────────────

interface TicketModalProps {
  ticket: SupportTicketDTO;
  tenantName?: string;
  onClose: () => void;
  onSave: (id: string, status: SupportTicketStatus, response: string) => Promise<void>;
}

const TicketModal: React.FC<TicketModalProps> = ({ ticket, tenantName, onClose, onSave }) => {
  const [status, setStatus] = useState<SupportTicketStatus>(ticket.status as SupportTicketStatus);
  const [response, setResponse] = useState(ticket.response ?? '');
  const [isSaving, setIsSaving] = useState(false);

  const hasChanges =
    status !== ticket.status || response !== (ticket.response ?? '');

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(ticket.id, status, response);
      onClose();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <ModalShell
      isOpen
      onClose={onClose}
      size="lg"
      title={ticket.title}
      subtitle={
        <>
          {ticket.userDisplayName ?? ticket.userEmail ?? ticket.userId}
          {ticket.userEmail && ticket.userDisplayName && (
            <span className="ml-2 text-xs">({ticket.userEmail})</span>
          )}
        </>
      }
      footer={
        <>
          <Button variant="primary" onClick={handleSave} disabled={!hasChanges || isSaving} isLoading={isSaving} className="w-full sm:w-auto">
            {isSaving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={isSaving} className="w-full sm:w-auto">
            Cancelar
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400">Clínica:</span>
            <p className="font-medium text-gray-700 dark:text-gray-300">{tenantName ?? '—'}</p>
            <p className="font-mono text-xs text-gray-400 dark:text-gray-500 break-all mt-0.5">{ticket.tenantId}</p>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400">Creado:</span>
            <p className="text-gray-700 dark:text-gray-300">
              {new Date(ticket.createdAt).toLocaleString('es-AR')}
            </p>
          </div>
          {ticket.respondedBy && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">Gestionado por:</span>
              <p className="font-medium text-gray-700 dark:text-gray-300">{ticket.respondedBy}</p>
              {ticket.respondedByEmail && (
                <p className="text-xs text-gray-400 dark:text-gray-500">{ticket.respondedByEmail}</p>
              )}
              {ticket.respondedAt && (
                <p className="text-xs text-gray-400 dark:text-gray-500">
                  {new Date(ticket.respondedAt).toLocaleString('es-AR')}
                </p>
              )}
            </div>
          )}
          {ticket.fileName && (
            <div>
              <span className="text-gray-500 dark:text-gray-400">Archivo adjunto:</span>
              <p className="text-gray-700 dark:text-gray-300 break-all">{ticket.fileName}</p>
            </div>
          )}
        </div>

        {ticket.description && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Descripción
            </label>
            <p className="text-sm text-gray-600 dark:text-gray-400 bg-slate-50 dark:bg-gray-900/50 rounded p-3 whitespace-pre-wrap">
              {ticket.description}
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Estado
          </label>
          <select
            value={status}
            onChange={e => setStatus(e.target.value as SupportTicketStatus)}
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="pendiente">Pendiente</option>
            <option value="en_proceso">En Proceso</option>
            <option value="resuelto">Resuelto</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Respuesta al usuario
          </label>
          <textarea
            value={response}
            onChange={e => setResponse(e.target.value)}
            rows={5}
            placeholder="Escribí la respuesta para el usuario..."
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 resize-none"
          />
        </div>
      </div>
    </ModalShell>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────

export const SupportPage: React.FC = () => {
  const { t } = useTranslation();
  const [filterStatus, setFilterStatus] = useState<SupportTicketStatus | 'todos'>('todos');
  const [filterTenantId, setFilterTenantId] = useState('');
  const [search, setSearch] = useState('');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicketDTO | null>(null);

  const { tickets, isLoading, error, refetch, update } = useTickets();
  const { tenants } = useTenants();
  const { success, error: showError } = useToast();

  // Map tenantId → nombre
  const tenantNames = useMemo(() => {
    const map = new Map<string, string>();
    tenants.forEach(t => map.set(String(t.id), t.nombre));
    return map;
  }, [tenants]);

  const filtered = useMemo(() => {
    return tickets.filter(t => {
      if (filterStatus !== 'todos' && t.status !== filterStatus) return false;
      if (filterTenantId && !t.tenantId.toLowerCase().includes(filterTenantId.toLowerCase()) &&
          !(tenantNames.get(t.tenantId) ?? '').toLowerCase().includes(filterTenantId.toLowerCase())) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          t.title.toLowerCase().includes(q) ||
          (t.userEmail ?? '').toLowerCase().includes(q) ||
          (t.userDisplayName ?? '').toLowerCase().includes(q) ||
          t.tenantId.toLowerCase().includes(q) ||
          (tenantNames.get(t.tenantId) ?? '').toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [tickets, filterStatus, filterTenantId, search, tenantNames]);

  const counts = useMemo(() => {
    return {
      todos: tickets.length,
      pendiente: tickets.filter(t => t.status === 'pendiente').length,
      en_proceso: tickets.filter(t => t.status === 'en_proceso').length,
      resuelto: tickets.filter(t => t.status === 'resuelto').length,
    };
  }, [tickets]);

  const handleSave = async (id: string, status: SupportTicketStatus, response: string) => {
    try {
      await update(id, { status, response: response.trim() || undefined });
      success(t('pages.support.updatedSuccess'));
    } catch {
      showError(t('pages.support.updateError'));
      throw new Error('update failed');
    }
  };

  return (
    <div className="panel-page panel-safe-bottom">
      {/* Page title */}
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">
            {t('pages.support.title')}
          </h1>
          <p className="panel-page-subtitle hidden sm:block">
            {t('pages.support.subtitle')}
          </p>
        </div>
        <button
          onClick={refetch}
          disabled={isLoading}
          className="btn btn-secondary w-full sm:w-auto"
        >
          {isLoading ? 'Actualizando...' : '↻ Actualizar'}
        </button>
      </div>

      {/* Status tabs */}
      <div className="flex gap-1.5 flex-wrap">
        {(['todos', 'pendiente', 'en_proceso', 'resuelto'] as const).map(s => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`panel-chip px-2.5 py-1.5 transition-colors ${
              filterStatus === s
                ? 'bg-teal-600 text-white'
                : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}
          >
            {s === 'todos' ? 'Todos' : STATUS_LABELS[s]}{' '}
            <span className="ml-0.5 font-normal opacity-75">({counts[s]})</span>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <input
          type="text"
          placeholder="Buscar por título, usuario o tenant..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 min-w-0 w-full sm:min-w-[12rem] rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
        <input
          type="text"
          placeholder="Filtrar por clínica..."
          value={filterTenantId}
          onChange={e => setFilterTenantId(e.target.value)}
          className="w-full sm:w-64 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
        />
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 text-sm text-red-700 dark:text-red-300">
          {error.message}
        </div>
      )}

      {/* Table */}
      <div className="panel-table-wrap">
        <p className="panel-table-scroll-hint">Deslizá horizontalmente para ver más columnas</p>
        {isLoading ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">Cargando tickets...</div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            {tickets.length === 0 ? 'No hay tickets de soporte.' : 'No hay tickets que coincidan con los filtros.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[640px] w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-slate-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticket
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Usuario
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                    Clínica
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Estado
                  </th>
                  <th className="px-4 py-3.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">
                    Fecha
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Acción
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filtered.map(ticket => (
                  <tr
                    key={ticket.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900 dark:text-white line-clamp-1">
                        {ticket.title}
                      </p>
                      {ticket.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mt-0.5">
                          {ticket.description}
                        </p>
                      )}
                      {ticket.response && (
                        <span className="text-xs text-green-600 dark:text-green-400">✓ Respondido</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-gray-900 dark:text-white">
                        {ticket.userDisplayName ?? '—'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-36">
                        {ticket.userEmail ?? ticket.userId}
                      </p>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell">
                      <p className="text-sm text-gray-700 dark:text-gray-300">
                        {tenantNames.get(ticket.tenantId) ?? ticket.tenantId}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          STATUS_COLORS[ticket.status as SupportTicketStatus] ??
                          'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {STATUS_LABELS[ticket.status as SupportTicketStatus] ?? ticket.status}
                      </span>
                      {ticket.respondedBy && (
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 whitespace-nowrap" title={ticket.respondedByEmail}>
                          👤 {ticket.respondedBy}
                        </p>
                      )}
                    </td>
                    <td className="px-4 py-3.5 hidden md:table-cell">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(ticket.createdAt).toLocaleDateString('es-AR')}
                      </p>
                      <p className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date(ticket.createdAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setSelectedTicket(ticket)}
                        className="text-sm text-teal-600 dark:text-teal-400 hover:text-teal-800 dark:hover:text-teal-300 font-medium transition-colors"
                      >
                        Ver / Responder
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary */}
      {!isLoading && filtered.length > 0 && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Mostrando {filtered.length} de {tickets.length} tickets
        </p>
      )}

      {/* Modal */}
      {selectedTicket && (
        <TicketModal
          ticket={selectedTicket}
          tenantName={tenantNames.get(selectedTicket.tenantId)}
          onClose={() => setSelectedTicket(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};
